/**
 * Wniosek o zmianę danych (spec 01 §3 — przyciski „Złóż wniosek o zmianę").
 *
 * Formularz nie zapisuje niczego w aktach: tworzy wniosek, który dział HR
 * zatwierdza albo odsyła do korekty (kryterium §17 pkt 2). Obecna wartość jest
 * pokazana obok pola, żeby autor widział, co dokładnie zmienia.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Modal, Select, Spinner, useToast } from '@k1p4/ui'
import { NFZ_BRANCHES, TAX_OFFICES, parseStreetLine } from '@k1p4/shared'
import { ApiError, api, type ChangeRequestField } from '../../api/client'
import styles from './ChangeRequestModal.module.css'

export interface ChangeRequestModalProps {
  open: boolean
  onClose: () => void
  employeeId: string
  field: ChangeRequestField
  /** Wartość obecna w aktach — pokazywana obok formularza (może być pusta). */
  currentValue: string | null
  onSubmitted?: () => void
}

/**
 * Wykazy, z których wybiera się wartość zamiast wpisywać ją słowami. Kod z
 * wykazu wchodzi wprost do dokumentu urzędowego — oddział NFZ do zgłoszenia
 * ZUS ZUA, urząd skarbowy do nagłówka informacji PIT-11 (Etap 27).
 */
const DICTIONARIES = {
  nfz: NFZ_BRANCHES,
  tax_offices: TAX_OFFICES,
} as const

/** Pola formularza per rodzaj zmiany — układ 1:1 z walidacją serwera. */
const FIELDS: Record<
  ChangeRequestField,
  Array<{ key: string; label: string; type?: string; dictionary?: keyof typeof DICTIONARIES }>
> = {
  address: [
    { key: 'addressStreet', label: 'addressStreet' },
    { key: 'addressCityZip', label: 'addressCityZip' },
  ],
  phone: [{ key: 'phone', label: 'phone' }],
  private_email: [{ key: 'privateEmail', label: 'privateEmail', type: 'email' }],
  bank_account: [
    { key: 'iban', label: 'iban' },
    { key: 'bankName', label: 'bankName' },
  ],
  tax_office: [{ key: 'taxOfficeCode', label: 'taxOfficeCode', dictionary: 'tax_offices' }],
  nfz_branch: [{ key: 'nfzBranch', label: 'nfzBranch', dictionary: 'nfz' }],
}

export function ChangeRequestModal({
  open,
  onClose,
  employeeId,
  field,
  currentValue,
  onSubmitted,
}: ChangeRequestModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const baseId = useId()

  const [values, setValues] = useState<Record<string, string>>({})
  const [comment, setComment] = useState('')
  const [attachment, setAttachment] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addressTouched, setAddressTouched] = useState(false)

  function close(): void {
    setValues({})
    setComment('')
    setAttachment(null)
    setError(null)
    setAddressTouched(false)
    onClose()
  }

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadFile(file),
    onSuccess: (stored) => setAttachment({ id: stored.id, name: stored.filename }),
    onError: () => setError('changeRequestForm.errorAttachment'),
  })

  const submit = useMutation({
    mutationFn: () =>
      api.createChangeRequest({
        employeeId,
        fieldKey: field,
        /* Puste pola nie jadą do API — brak wartości to nie pusty string. */
        value: Object.fromEntries(
          Object.entries(values)
            .map(([key, value]) => [key, value.trim()] as const)
            .filter(([, value]) => value.length > 0),
        ),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(attachment ? { attachmentFileId: attachment.id } : {}),
      }),
    onSuccess: () => {
      toast.show(t('changeRequestForm.submitted'), { tone: 'success' })
      onSubmitted?.()
      close()
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'CHANGE_REQUEST_PENDING'
            ? 'changeRequestForm.errorPending'
            : caught.code === 'VALIDATION_ERROR'
              ? 'changeRequestForm.errorValidation'
              : 'changeRequestForm.errorGeneric',
        )
      } else {
        setError('changeRequestForm.errorGeneric')
      }
    },
  })

  /* Ta sama gramatyka adresu co w zgłoszeniach ZUS — zapis niejednoznaczny nie przejdzie (#7). */
  const addressInvalid =
    field === 'address' &&
    (values.addressStreet ?? '').trim().length > 0 &&
    parseStreetLine(values.addressStreet) === null
  /* Błąd pokazujemy po opuszczeniu pola, a nie od pierwszego znaku. */
  const showAddressError = addressInvalid && addressTouched

  const inputs = FIELDS[field]
  /* Pierwsze pole jest wymagane; reszta (np. nazwa banku) bywa opcjonalna. */
  const ready = (values[inputs[0]!.key] ?? '').trim().length > 0

  return (
    <Modal open={open} onClose={close} title={t('changeRequestForm.title')}>
      <p className={styles.subtitle}>{t('changeRequestForm.subtitle')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <dl className={styles.current}>
        <dt>{t('changeRequestForm.field')}</dt>
        <dd>{t(`requestFields.${field}`)}</dd>
        <dt>{t('changeRequestForm.currentValue')}</dt>
        <dd className={currentValue ? undefined : styles.muted}>
          {currentValue ?? t('changeRequests.empty_value')}
        </dd>
      </dl>

      {inputs.map((input) => (
        <FormField
          key={input.key}
          label={t(`changeRequestForm.${input.label}`)}
          htmlFor={`${baseId}-${input.key}`}
          {...(input.key === 'addressStreet'
            ? showAddressError
              ? { error: t('validation.addressStreet.ambiguous') }
              : { hint: t('validation.addressStreet.hint') }
            : {})}
        >
          {input.dictionary === undefined ? (
            <Input
              id={`${baseId}-${input.key}`}
              type={input.type ?? 'text'}
              value={values[input.key] ?? ''}
              onChange={(event) => {
                setValues((current) => ({ ...current, [input.key]: event.target.value }))
                setError(null)
              }}
              onBlur={() => {
                if (input.key === 'addressStreet') setAddressTouched(true)
              }}
            />
          ) : (
            <Select
              id={`${baseId}-${input.key}`}
              value={values[input.key] ?? ''}
              onChange={(event) => {
                setValues((current) => ({ ...current, [input.key]: event.target.value }))
                setError(null)
              }}
            >
              <option value="">{t('changeRequestForm.selectPlaceholder')}</option>
              {DICTIONARIES[input.dictionary].map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      ))}

      <FormField
        label={t('changeRequestForm.comment')}
        htmlFor={`${baseId}-comment`}
      >
        <textarea
          id={`${baseId}-comment`}
          className={styles.textarea}
          rows={3}
          placeholder={t('changeRequestForm.commentPlaceholder')}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </FormField>

      <FormField
        label={t('changeRequestForm.attachment')}
        htmlFor={`${baseId}-file`}
        hint={attachment ? attachment.name : t('changeRequestForm.attachmentHint')}
      >
        <input
          id={`${baseId}-file`}
          className={styles.file}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          onChange={(event) => {
            const file = event.target.files?.[0]
            setError(null)
            if (file) upload.mutate(file)
          }}
        />
      </FormField>

      <div className={styles.actions}>
        <Button variant="secondary" size="lg" onClick={close}>
          {t('changeRequests.cancel')}
        </Button>
        <Button
          size="lg"
          className={styles.grow}
          disabled={!ready || addressInvalid || submit.isPending || upload.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? (
            <Spinner label={t('changeRequestForm.submitting')} />
          ) : upload.isPending ? (
            <Spinner label={t('changeRequestForm.attachmentUploading')} />
          ) : (
            t('changeRequestForm.submit')
          )}
        </Button>
      </div>
    </Modal>
  )
}
